import { CSRService } from "./CSRService";

export type MigrationStatus =
    | "discovering"
    | "running"
    | "stopping"
    | "paused"
    | "completed"
    | "completed_with_errors"
    | "failed";

export interface MediaMigrationRun {
    id: string;
    status: MigrationStatus;
    total_records: number;
    completed_records: number;
    failed_records: number;
    total_bytes: number;
    completed_bytes: number;
    percentage: number;
    elapsed_seconds: number;
    eta_seconds: number | null;
    throughput_bytes_per_second: number;
    created_datetime: string;
    started_datetime: string | null;
    updated_datetime: string;
    completed_datetime: string | null;
    error_message: string | null;
}

export interface MediaMigrationItem {
    id: string;
    attachment_id: string;
    status: string;
    attempts: number;
    size_bytes: number;
    error_message: string | null;
}

export type MigrationEventLevel = "info" | "success" | "warning" | "error";

export interface MediaMigrationEvent {
    id: string;
    run_id: string;
    item_id: string | null;
    attachment_id: string | null;
    level: MigrationEventLevel;
    event_type: string;
    message: string;
    bytes_transferred: number | null;
    created_datetime: string;
}

export interface MediaMigrationDiagnostics {
    environment: string;
    api_url: string | null;
    cors_origins: string[];
    media_s3_bucket_configured: boolean;
    media_s3_bucket: string | null;
    aws_media_region: string;
    s3_endpoint: string;
    tables: Record<string, { exists: boolean; count: number | null; error?: string }>;
    cloudinary_attachment_count: number | null;
    latest_run: {
        id: string;
        status: string;
        created_datetime: string;
        updated_datetime: string;
        error_message: string | null;
    } | null;
    errors: string[];
}

export class MediaMigrationService extends CSRService {
    public getCurrent = async (): Promise<MediaMigrationRun | null> => {
        const response = await MediaMigrationService.axiosInstance.get<MediaMigrationRun | null>(
            "/api/media-migrations/current",
        );
        return response.data;
    };

    public start = async (password: string): Promise<MediaMigrationRun> => {
        const response = await MediaMigrationService.axiosInstance.post<MediaMigrationRun>("/api/media-migrations", {
            password,
        });
        return response.data;
    };

    public stop = async (runId: string, password: string): Promise<MediaMigrationRun> => {
        const response = await MediaMigrationService.axiosInstance.post<MediaMigrationRun>(
            `/api/media-migrations/${runId}/stop`,
            { password },
        );
        return response.data;
    };

    public resume = async (runId: string, password: string): Promise<MediaMigrationRun> => {
        const response = await MediaMigrationService.axiosInstance.post<MediaMigrationRun>(
            `/api/media-migrations/${runId}/resume`,
            { password },
        );
        return response.data;
    };

    public getFailedItems = async (runId: string): Promise<MediaMigrationItem[]> => {
        const response = await MediaMigrationService.axiosInstance.get<MediaMigrationItem[]>(
            `/api/media-migrations/${runId}/items`,
            { params: { status: "failed", limit: 50 } },
        );
        return response.data;
    };

    public getEvents = async (runId: string, level?: MigrationEventLevel): Promise<MediaMigrationEvent[]> => {
        const response = await MediaMigrationService.axiosInstance.get<MediaMigrationEvent[]>(
            `/api/media-migrations/${runId}/events`,
            { params: { level, limit: 100 } },
        );
        return response.data;
    };

    public getDiagnostics = async (): Promise<MediaMigrationDiagnostics> => {
        const response = await MediaMigrationService.axiosInstance.get<MediaMigrationDiagnostics>(
            "/api/media-migrations/diagnostics",
        );
        return response.data;
    };
}

export const mediaMigrationService = new MediaMigrationService();
